import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
  TextField,
  Rating,
} from "@mui/material";

type FeatureFeedbackProps = {
  onSubmit: (data: {
    rating: number;
    feedback: string;
  }) => Promise<void> | void;
  onSkip?: () => void;
  onClose?: () => void;
  initialRating?: number;
  initialFeedback?: string;
  submitting?: boolean;
};

const FeatureFeedback = (props: FeatureFeedbackProps) => {
  const {
    onSubmit,
    onSkip,
    onClose,
    initialRating,
    initialFeedback,
    submitting,
  } = props;
  const [rating, setRating] = useState<number | null>(initialRating ?? null);
  const [feedback, setFeedback] = useState(initialFeedback ?? "");
  const [touched, setTouched] = useState(false);
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);
  const [sent, setSent] = useState(false);

  const hasError = touched && (rating === null || rating === 0);

  const handleSubmit = async () => {
    if (sent) return;
    setTouched(true);
    if (!rating || rating === 0) return;
    setSent(true);
    await onSubmit({ rating, feedback });
  };

  return (
    <>
      <Alert
        severity="success"
        sx={{
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Stack
          alignItems="center"
          direction="column"
          spacing={3}
          sx={{ width: "100%" }}
        >
          {sent ? (
            <Stack spacing={2} alignItems="center" sx={{ width: "100%" }}>
              <Typography textAlign="center" variant="h5">
                Obrigado!
              </Typography>
              <Typography textAlign="center" variant="body2">
                Sua avaliação foi enviada com sucesso.
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={() => onClose?.()}
                type="button"
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Fechar
              </Button>
            </Stack>
          ) : (
            <>
              <Stack spacing={1} alignItems="center">
                <Typography textAlign="center" variant="h5">
                  Muito bem!
                </Typography>
                <Typography textAlign="center" variant="body2">
                  Solicitação enviada com sucesso.
                </Typography>
              </Stack>

              <Stack
                spacing={2}
                sx={{
                  width: "100%",
                }}
              >
                <Typography variant="body2" fontWeight={600} textAlign="center">
                  Como você avalia esta funcionalidade?
                </Typography>

                <Stack spacing={1} alignItems="center" sx={{ width: "100%" }}>
                  <Typography variant="body2" textAlign="center">
                    Avalie de 1 a 5:
                  </Typography>
                  <Rating
                    value={rating || 0}
                    onChange={(_, newValue) => {
                      setRating(newValue);
                      if (!touched) setTouched(true);
                    }}
                    max={5}
                    size="large"
                    sx={{
                      "& .MuiRating-icon": {
                        fontSize: { xs: "2rem", sm: "2.5rem" },
                      },
                    }}
                  />
                  {hasError && (
                    <Alert severity="error" sx={{ mt: 1, width: "100%" }}>
                      Por favor, avalie a funcionalidade
                    </Alert>
                  )}
                </Stack>

                <TextField
                  variant="filled"
                  multiline
                  rows={4}
                  placeholder="Encontrou algum problema ou tem uma sugestão? Conta pra gente!"
                  fullWidth
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                />

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  {onSkip && (
                    <Button
                      size="small"
                      onClick={() => setSkipConfirmOpen(true)}
                      type="button"
                      disabled={submitting}
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                        flex: { xs: 1, sm: "0 1 auto" },
                      }}
                    >
                      Pular
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSubmit}
                    type="button"
                    disabled={submitting || sent}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      flex: { xs: 1, sm: "0 1 auto" },
                    }}
                  >
                    Enviar Avaliação
                  </Button>
                </Stack>
              </Stack>
            </>
          )}
        </Stack>
      </Alert>

      <Dialog open={skipConfirmOpen} onClose={() => setSkipConfirmOpen(false)}>
        <DialogTitle>Confirmar</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deseja mesmo pular? Sua avaliação é muito importante para nós
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipConfirmOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => {
              setSkipConfirmOpen(false);
              onSkip?.();
            }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FeatureFeedback;
